import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Tag,
  Info,
  CheckCircle2,
  Ticket,
  AlertCircle,
  Minus,
  Plus,
  ShoppingCart,
} from "lucide-react";
import { Navbar } from "../../components/Navbar";
import TheDRANavbar from "../../components/TheDRANavbar";
import { TheDRALogo } from "../../components/TheDRANavbar/TheDRALogo";
import { Footer } from "../../components/Footer";
import "../OrganizationHomepageTheDRA/styles.scss";
import { CheckoutModal } from "../../components/CheckoutModal";
import { getEventUrl } from "../../lib/utils/domain";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { IEventProps, IEventTicketDetailsProps } from "../../types";
import { FullPageLoader } from "../../components/ui/loader";
import {
  useUser,
  getOrgTokenKey,
  getOrgUserProfileKey,
} from "../../context/UserContext";
import { IOrganizationProps } from "../../types/Organization";
import { getCurrencySymbol } from "../../lib/data/currencies";
import {
  isCustomDomain,
  getOrganizationSlugFromSubdomain,
  isSubdomain,
} from "../../lib/utils/domain";

interface TicketTier {
  id: string;
  name: string;
  price: number;
  description: string;
  remaining: number;
  total: number;
  features: string[];
  color: string;
  popular?: boolean;
  maxOrderLimit?: number | null;
  minOrderLimit?: number;
  currency?: string;
}

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  image: string;
  category: string;
  attendees: number;
  maxAttendees?: number;
  ticketTiers: TicketTier[];
  fullDescription?: string;
  included?: string[];
}

// Extended event data type that might include additional fields from API
type ExtendedEventData = IEventProps & {
  start_time?: string;
  end_time?: string;
  category?: string;
  features_included?: string;
};

// Convert IEventProps to Event format
const convertToEvent = (
  eventData: ExtendedEventData,
  tickets: IEventTicketDetailsProps[] = []
): Event => {
  // Format time from start_time and end_time
  const formatTime = (timeString: string): string => {
    if (!timeString) return "";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      if (isNaN(hour)) return "";
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes || "00"} ${ampm}`;
    } catch {
      return "";
    }
  };

  // Get time range from event data
  const startTime = eventData.start_time
    ? formatTime(eventData.start_time)
    : "";
  const endTime = eventData.end_time ? formatTime(eventData.end_time) : "";
  const timeDisplay =
    startTime && endTime
      ? `${startTime} - ${endTime}`
      : startTime || "Time TBD";

  // Format location
  const locationString = eventData.location
    ? [
        eventData.location.address,
        eventData.location.city,
        eventData.location.state,
        eventData.location.country,
      ]
        .filter(Boolean)
        .join(", ") ||
      (eventData.type === "online"
        ? "Online Event"
        : eventData.type === "hybrid"
        ? "Hybrid Event"
        : "Location TBA")
    : eventData.type === "online"
    ? "Online Event"
    : "Location TBA";

  // Convert tickets to ticket tiers
  const ticketTiers: TicketTier[] = tickets.map((ticket, index) => {
    const price = parseFloat(ticket.price) || 0;
    // Remaining tickets: quantity represents total available
    // In a real scenario, you'd subtract tickets sold from quantity
    const remaining = ticket.quantity || 0;

    return {
      id: ticket.id.toString(),
      name: ticket.ticket_type,
      price: price,
      description:
        ticket.instructions || `Get access to ${ticket.ticket_type} tier`,
      remaining: Math.max(0, remaining),
      total: ticket.quantity,
      maxOrderLimit: ticket.max_order_limit,
      minOrderLimit: ticket.min_order_limit,
      features: ticket.instructions
        ? ticket.instructions
            .split(",")
            .map((f) => f.trim())
            .filter(Boolean)
        : [],
      color: index === 0 ? "#8b5cf6" : index === 1 ? "#3b82f6" : "#10b981",
      popular: index === 0,
      currency: ticket.currency || "USD",
    };
  });

  // Calculate max attendees from sum of all ticket quantities
  const maxAttendeesFromTickets = ticketTiers.reduce(
    (sum, tier) => sum + (tier.total || 0),
    0
  );

  // Parse features_included if available (from API or description)
  const included = eventData.features_included
    ? eventData.features_included
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean)
    : eventData.event_description
    ? eventData.event_description
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean)
        .slice(0, 5)
    : [];

  return {
    id: eventData.id.toString(),
    title: eventData.name,
    description:
      eventData.event_description || eventData.organizer_description || "",
    date: eventData.date,
    time: timeDisplay,
    location: locationString,
    image: eventData.cover_image || "",
    category: eventData.category || "General",
    attendees: eventData.event_attendees_count || 0,
    maxAttendees:
      ticketTiers.length > 0
        ? maxAttendeesFromTickets || undefined
        : eventData.number_of_seats || undefined,
    ticketTiers:
      ticketTiers.length > 0
        ? ticketTiers
        : [
            {
              id: "default",
              name: "General Admission",
              price: 0,
              description: "Free admission",
              remaining: eventData.number_of_seats || 0,
              total: eventData.number_of_seats || 0,
              features: [],
              color: "#8b5cf6",
              currency: "USD",
            },
          ],
    fullDescription: eventData.organizer_description,
    included: included,
  };
};

interface EventDetailsPageProps {
  onLoginClick?: (organizationSlug: string) => void;
}

const SingleEvent: React.FC<EventDetailsPageProps> = ({ onLoginClick }) => {
  const { eventID } = useParams<{ eventID: string }>();
  const { organizationSlug: urlOrganizationSlug } = useParams<{
    organizationSlug?: string;
  }>();

  const navigate = useNavigate();
  const { userProfile, fetchUserProfile, setUserProfile } = useUser();

  // Get organization slug from subdomain if on a subdomain
  const subdomainSlug = getOrganizationSlugFromSubdomain();
  const isOnSubdomain = isSubdomain();
  const [selectedTickets, setSelectedTickets] = useState<
    Record<string, number>
  >({});
  const [showCheckout, setShowCheckout] = useState(false);

  // Fetch event data from API
  const { data: eventResponse, isLoading } = usePerformRequest<{
    event: ExtendedEventData;
    ticket_sales?: IEventTicketDetailsProps[];
    organization?: {
      id?: number;
      name: string;
      logo: string;
      email?: string;
      phone_number?: string;
      linkedin_link?: string;
      slug?: string;
      location?: {
        address: string;
        city: string;
        state: string;
        country: string;
      };
    };
  }>({
    method: Endpoints.GetSingleEvent(eventID || "").method,
    route: Endpoints.GetSingleEvent(eventID || "").route,
    doNotUseAuthorization: true,
  });

  // Convert API response to Event format
  const event: Event | null = useMemo(() => {
    if (!eventResponse?.event) return null;
    return convertToEvent(
      eventResponse.event,
      eventResponse.ticket_sales || []
    );
  }, [eventResponse]);

  // Get organization slug - prioritize subdomain, then URL, then event response
  const organizationSlug = useMemo(() => {
    // First check if we're on a subdomain
    if (subdomainSlug) {
      return subdomainSlug;
    }
    // Then check URL params (for regular domain routing)
    if (urlOrganizationSlug) {
      return urlOrganizationSlug;
    }
    // If on custom domain (not subdomain), try to get slug from event response
    if (isCustomDomain() && !isOnSubdomain && eventResponse?.organization) {
      return (eventResponse.organization as any)?.slug || "";
    }
    return "";
  }, [subdomainSlug, urlOrganizationSlug, eventResponse, isOnSubdomain]);

  // Get organization data from response or event
  const organizationData = useMemo(() => {
    if (eventResponse?.organization) {
      const org = eventResponse.organization;
      return {
        name: org.name || "Organization",
        logoImage: org.logo || "",
        email: org.email || "",
        phone: org.phone_number || "",
        linkedinLink: org.linkedin_link || "",
        location: org.location
          ? [
              org.location.address,
              org.location.city,
              org.location.state,
              org.location.country,
            ]
              .filter(Boolean)
              .join(", ")
          : "",
        slug: org.slug || organizationSlug || "",
      };
    }
    // Fallback to event data
    if (eventResponse?.event) {
      const event = eventResponse.event;
      const userName =
        event.user?.first_name && event.user?.last_name
          ? `${event.user.first_name} ${event.user.last_name}`
          : event.host_name || "Event Host";

      return {
        name: userName,
        logoImage: "",
        email: event.user?.email || "",
        phone: event.user?.phone_number ? String(event.user.phone_number) : "",
        linkedinLink: "",
        location: event.location
          ? [
              event.location.address,
              event.location.city,
              event.location.state,
              event.location.country,
            ]
              .filter(Boolean)
              .join(", ")
          : "",
        slug: organizationSlug || "",
      };
    }
    return {
      name: "Organization",
      logoImage: "",
      email: "",
      phone: "",
      linkedinLink: "",
      location: "",
      slug: organizationSlug || "",
    };
  }, [eventResponse, organizationSlug]);

  // Check if this is a TheDRA event
  const isTheDRA = useMemo(() => {
    const orgName = organizationData.name?.toLowerCase() || "";
    const orgSlug =
      organizationSlug?.toLowerCase() ||
      organizationData.slug?.toLowerCase() ||
      "";
    return (
      orgSlug === "thedra" ||
      orgName.includes("digital risk alliance") ||
      orgName.includes("thedra")
    );
  }, [organizationData.name, organizationData.slug, organizationSlug]);

  // Get homepage URL for TheDRA navbar
  const thedraHomepageUrl = useMemo(() => {
    if (!isTheDRA) return "";
    if (isCustomDomain() && !isOnSubdomain) {
      return "/";
    }
    if (organizationSlug) {
      return `/${organizationSlug}`;
    }
    return "/thedra";
  }, [isTheDRA, isCustomDomain, isOnSubdomain, organizationSlug]);

  // Set dynamic document title
  useEffect(() => {
    if (event) {
      document.title = `${event.title} - ${organizationData.name}`;
    }
  }, [event, organizationData.name]);

  // Helper function to construct organization object from event response
  const constructOrganization = (
    org: any,
    slug: string
  ): IOrganizationProps => {
    return {
      id: org.id,
      slug: slug,
      name: org.name || "Organization",
      subtitle: "",
      description: "",
      number_of_locations: 0,
      years_of_experience: 0,
      instagram_link: "",
      facebook_link: "",
      linkedin_link: "",
      x_link: "",
      youtube_link: "",
      phone_number: org.phone_number || "",
      website: "",
      email: org.email || "",
      created_at: "",
      updated_at: "",
      logo: org.logo || "",
      location: org.location
        ? {
            address: org.location.address || "",
            city: org.location.city || "",
            state: org.location.state || "",
            country: org.location.country || "",
            postal_code: "",
          }
        : {
            address: "",
            city: "",
            state: "",
            country: "",
            postal_code: "",
          },
      about: {
        mission: "",
        vision: "",
        values: "",
        about: "",
      },
      event_count: 0,
      event_attendees_count: 0,
    };
  };

  // Load profile immediately if we have organizationSlug from URL or subdomain
  // This ensures Navbar shows logged-in state immediately
  useEffect(() => {
    // Use subdomain slug if available, otherwise use URL slug
    const slugToCheck = subdomainSlug || urlOrganizationSlug;
    if (!slugToCheck) return;

    const tokenKey = getOrgTokenKey(slugToCheck);
    const token = localStorage.getItem(tokenKey);

    if (!token) return;

    // Check if current userProfile in context matches this organization
    if (userProfile?.organizationSlug === slugToCheck) {
      return; // Already have the correct profile loaded
    }

    const profileKey = getOrgUserProfileKey(slugToCheck);
    const storedProfile = localStorage.getItem(profileKey);

    // If we have a stored profile, load it immediately
    if (storedProfile) {
      try {
        const profile = JSON.parse(storedProfile);
        if (profile.organizationSlug === slugToCheck) {
          setUserProfile(profile);
          return;
        }
      } catch (e) {
        // Invalid stored profile, will be handled below
      }
    }
  }, [subdomainSlug, urlOrganizationSlug, userProfile, setUserProfile]);

  // Check for authentication token and profile for this organization
  // Load profile when we have organizationSlug (from URL or eventResponse)
  useEffect(() => {
    if (!organizationSlug) return;

    const tokenKey = getOrgTokenKey(organizationSlug);
    const token = localStorage.getItem(tokenKey);

    if (!token) return;

    // Check if current userProfile in context matches this organization
    if (userProfile?.organizationSlug === organizationSlug) {
      return; // Already have the correct profile loaded
    }

    const profileKey = getOrgUserProfileKey(organizationSlug);
    const storedProfile = localStorage.getItem(profileKey);

    // If we have a stored profile, load it immediately (this ensures Navbar shows logged in state)
    if (storedProfile) {
      try {
        const profile = JSON.parse(storedProfile);
        if (profile.organizationSlug === organizationSlug) {
          setUserProfile(profile);
          // If we have organization data from eventResponse, fetch fresh profile to update any changes
          if (eventResponse?.organization) {
            const org = eventResponse.organization;
            if (org.id) {
              const organization = constructOrganization(org, organizationSlug);
              fetchUserProfile(organizationSlug, organization);
            }
          }
          return;
        }
      } catch (e) {
        // Invalid stored profile, will fetch new one below
      }
    }

    // If we have organization data from eventResponse, fetch fresh profile
    if (eventResponse?.organization) {
      const org = eventResponse.organization;
      if (org.id) {
        const organization = constructOrganization(org, organizationSlug);
        fetchUserProfile(organizationSlug, organization);
      }
    }
  }, [
    organizationSlug,
    eventResponse,
    fetchUserProfile,
    userProfile,
    setUserProfile,
  ]);

  // Loading state
  if (isLoading || !eventResponse) {
    return <FullPageLoader label="Loading event..." />;
  }

  // Error state - event not found
  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-[#17265a] text-4xl mb-4">Event Not Found</h1>
          <button
            onClick={() => navigate("/")}
            className="bg-[#17265a] text-white px-6 py-3 rounded-lg hover:bg-[#0f1a3f] transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const handleQuantityChange = (tierId: string, change: number) => {
    setSelectedTickets((prev) => {
      const tier = event.ticketTiers.find((t) => t.id === tierId);
      if (!tier) return prev;

      const remaining = tier.remaining;
      const rawMax = tier.maxOrderLimit ?? undefined;
      const perOrderLimit =
        rawMax && rawMax > 0 ? Math.min(rawMax, remaining) : remaining;

      const currentQty = prev[tierId] || 0;
      const newQty = Math.max(0, Math.min(currentQty + change, perOrderLimit));

      if (newQty === 0) {
        const { [tierId]: _, ...rest } = prev;
        return rest;
      }

      return { ...prev, [tierId]: newQty };
    });
  };

  const totalAmount = Object.entries(selectedTickets).reduce(
    (total, [tierId, qty]) => {
      const tier = event.ticketTiers.find((t) => t.id === tierId);
      return total + (tier ? tier.price * qty : 0);
    },
    0
  );

  const totalTickets = Object.values(selectedTickets).reduce(
    (sum, qty) => sum + qty,
    0
  );

  const progress = event.maxAttendees
    ? (event.attendees / event.maxAttendees) * 100
    : 0;
  const availability = event.maxAttendees
    ? ((event.maxAttendees - event.attendees) / event.maxAttendees) * 100
    : 100;

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      {isTheDRA ? (
        <TheDRANavbar
          homepageUrl={thedraHomepageUrl}
          organizationSlug={organizationSlug || "thedra"}
          onLoginClick={() => {
            if (onLoginClick && organizationSlug) {
              onLoginClick(organizationSlug);
            }
          }}
        />
      ) : (
        <Navbar
          organizationName={organizationData.name}
          logoImage={organizationData.logoImage}
          onLoginClick={() => {
            if (onLoginClick && organizationSlug) {
              onLoginClick(organizationSlug);
            }
          }}
        />
      )}

      {/* Main Content */}
      <div className="pt-20">
        {/* Hero Section */}
        <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />

          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/")}
            className="absolute top-8 left-8 px-4 py-2 bg-white/95 backdrop-blur-md rounded-lg flex items-center gap-2 text-gray-700 hover:bg-white transition-colors shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </motion.button>

          {/* Category Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="absolute top-8 right-8"
          >
            <div className="px-4 py-2 rounded-lg bg-white/95 backdrop-blur-md shadow-lg border border-white/50">
              <span className="text-sm text-[#17265a]">{event.category}</span>
            </div>
          </motion.div>

          {/* Title and Quick Info */}
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
            <div className="max-w-7xl mx-auto">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-white text-4xl lg:text-5xl mb-6"
              >
                {event.title}
              </motion.h1>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap gap-6 text-white"
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>
                    {new Date(event.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{event.location}</span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Event Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Availability Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-[#17265a]" />
                  <h3 className="text-[#17265a]">Availability</h3>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                  <span>
                    {event.attendees.toLocaleString()} /{" "}
                    {event.maxAttendees?.toLocaleString() || "∞"} attending
                  </span>
                  <span
                    className={`px-3 py-1 rounded-lg ${
                      availability > 20
                        ? "bg-green-100 text-green-700"
                        : availability > 10
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {availability > 20
                      ? "Good Availability"
                      : availability > 10
                      ? "Limited Spots"
                      : "Almost Full"}
                  </span>
                </div>

                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1, delay: 0.6 }}
                    className={`h-full rounded-full ${
                      availability > 20
                        ? "bg-green-500"
                        : availability > 10
                        ? "bg-yellow-500"
                        : "bg-red-500"
                    }`}
                  />
                </div>
              </motion.div>

              {/* About Event */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-xl p-6 border border-gray-200"
              >
                <h3 className="text-[#17265a] mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  About This Event
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {event.description}
                </p>
                {event.fullDescription && (
                  <p className="text-gray-700 leading-relaxed">
                    {event.fullDescription}
                  </p>
                )}
              </motion.div>

              {/* What's Included */}
              {event.included && event.included.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="bg-white rounded-xl p-6 border border-gray-200"
                >
                  <h3 className="text-[#17265a] mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {event.included.map((item, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 + index * 0.1 }}
                        className="flex items-start gap-3 text-gray-700"
                      >
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>

            {/* Right Column - Ticket Selection */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <h3 className="text-gray-900 mb-4 flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-[#17265a]" />
                    Select Tickets
                  </h3>

                  <div className="space-y-4 mb-6">
                    {event.ticketTiers.map((tier, index) => (
                      <motion.div
                        key={tier.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 + index * 0.1 }}
                        className={`relative bg-white rounded-lg border-2 ${
                          tier.popular
                            ? "border-[#17265a] shadow-lg"
                            : "border-gray-200"
                        } p-5 hover:shadow-md transition-shadow`}
                      >
                        {tier.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#17265a] text-white text-xs rounded-full">
                            Most Popular
                          </div>
                        )}

                        <div className="mb-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-gray-900">{tier.name}</h4>
                            <div className="text-right">
                              <div
                                className="text-xl text-[#17265a]"
                                style={{ fontWeight: "700" }}
                              >
                                {getCurrencySymbol(tier.currency)}
                                {tier.price}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {tier.description}
                          </p>

                          {/* Remaining Tickets */}
                          <div className="flex items-center gap-2 text-xs">
                            {tier.remaining > 0 ? (
                              <>
                                <AlertCircle className="w-4 h-4 text-gray-400" />
                                <span
                                  className={`${
                                    tier.remaining < 20
                                      ? "text-orange-600"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {tier.remaining} tickets remaining
                                </span>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-4 h-4 text-red-500" />
                                <span className="text-red-600">Sold Out</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Features */}
                        {tier.features && tier.features.length > 0 && (
                          <div className="mb-4 pb-4 border-b border-gray-200">
                            <ul className="space-y-2">
                              {tier.features.map((feature, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2 text-xs text-gray-600"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Quantity Selector */}
                        {tier.remaining > 0 ? (
                          // User can select up to the lesser of remaining tickets and max_order_limit (if set)
                          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleQuantityChange(tier.id, -1)}
                              disabled={!selectedTickets[tier.id]}
                              className="w-8 h-8 rounded-md bg-white border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </motion.button>

                            <span className="text-gray-900 min-w-[2rem] text-center">
                              {selectedTickets[tier.id] || 0}
                            </span>

                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleQuantityChange(tier.id, 1)}
                              disabled={(() => {
                                const rawMax = tier.maxOrderLimit ?? undefined;
                                const perOrderLimit =
                                  rawMax && rawMax > 0
                                    ? Math.min(rawMax, tier.remaining)
                                    : tier.remaining;
                                return (
                                  (selectedTickets[tier.id] || 0) >=
                                  perOrderLimit
                                );
                              })()}
                              className="w-8 h-8 rounded-md bg-white border border-gray-300 flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </motion.button>
                          </div>
                        ) : (
                          <div className="bg-gray-100 rounded-lg p-3 text-center text-sm text-gray-500">
                            Sold Out
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>

                  {/* Checkout Summary */}
                  {totalTickets > 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-[#17265a] text-white rounded-lg p-6 shadow-xl"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <div className="text-sm text-white/80 mb-1">
                            Total Tickets
                          </div>
                          <div
                            className="text-2xl"
                            style={{ fontWeight: "700" }}
                          >
                            {totalTickets}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-white/80 mb-1">
                            Total Amount
                          </div>
                          <div
                            className="text-2xl"
                            style={{ fontWeight: "700" }}
                          >
                            {/* {event.ticketTiers.length > 0 && event.ticketTiers[0].currency
                              ? getCurrencySymbol(event.ticketTiers[0].currency)
                              : "$"} */}
                            {totalAmount.toFixed(2)}
                          </div>
                        </div>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowCheckout(true)}
                        className="w-full bg-white text-[#17265a] py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-md"
                      >
                        <ShoppingCart className="w-5 h-5" />
                        <span>Proceed to Checkout</span>
                      </motion.button>
                    </motion.div>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      {isTheDRA ? (
        <footer className="thedra-footer">
          <div className="thedra-container">
            <div className="thedra-footer-content">
              <div className="thedra-footer-left">
                <div className="thedra-footer-logo">
                  <TheDRALogo />
                </div>
                <p className="thedra-footer-email">
                  {organizationData.email || "info@TheDRA.org"}
                </p>
                <a
                  href={organizationData.linkedinLink || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="thedra-footer-linkedin"
                >
                  in
                </a>
              </div>
              <div className="thedra-footer-right">
                <div className="thedra-footer-links">
                  <a href="#privacy" className="thedra-footer-link">
                    Privacy Policy
                  </a>
                  <a href="#accessibility" className="thedra-footer-link">
                    Accessibility Statement
                  </a>
                </div>
                <p className="thedra-footer-copyright">
                  © {new Date().getFullYear()} by{" "}
                  {organizationData.name ||
                    "The Digital Risk Alliance (TheDRA)"}
                  .
                </p>
              </div>
            </div>
          </div>
        </footer>
      ) : (
        <Footer
          organizationName={organizationData.name}
          email={organizationData.email}
          phone={organizationData.phone}
          location={organizationData.location}
          logoImage={organizationData.logoImage}
        />
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        eventId={parseInt(eventID || "0")}
        eventTitle={event.title}
        selectedTickets={selectedTickets}
        ticketTiers={event.ticketTiers}
        totalAmount={totalAmount}
        totalTickets={totalTickets}
        organizationSlug={organizationSlug as string}
        isPaidEvent={eventResponse?.event?.is_paid_event || false}
      />
    </div>
  );
};

export default SingleEvent;
